import { Map as MapboxMap, RasterTileSource } from 'mapbox-gl'

import { TILESET_SOURCE } from './mapboxConstants'

const BOUNDS_WAIT_MS = 3000
const BOUNDS_POLL_MS = 100

/** Where the map has to be for a tileset's whole footprint to fill the canvas. */
export interface ITilesetFitCamera {
  center: [number, number]
  zoom: number
}

const toLngLat = (center: unknown): [number, number] => {
  const pair = Array.isArray(center)
    ? [center[0], center[1]]
    : [(center as { lng?: number })?.lng, (center as { lat?: number })?.lat]
  return Number.isFinite(pair[0]) && Number.isFinite(pair[1])
    ? [pair[0], pair[1]]
    : null
}

/**
 * The camera that fits a tileset's own footprint, or null until its source has loaded.
 *
 * Both the centre and the zoom come from the bounds in the tileset's TileJSON, solved against the
 * current bearing and pitch, because the Mapbox listing's centre zoom is the recipe minzoom and
 * an axis-aligned fit overflows a rotated viewport. STX1-1008.
 */
export const getTilesetFitCamera = (
  map: MapboxMap,
  tilesetId: string,
  camera: { bearing: number; pitch: number },
): ITilesetFitCamera => {
  const bounds = map?.getSource<RasterTileSource>(
    TILESET_SOURCE + tilesetId,
  )?.bounds

  if (bounds?.length !== 4) {
    return null
  }
  const [west, south, east, north] = bounds
  if (!(west < east) || !(south < north)) {
    return null
  }

  const solved = map.cameraForBounds(
    [
      [west, south],
      [east, north],
    ],
    camera,
  )
  const center = toLngLat(solved?.center)
  return center && Number.isFinite(solved.zoom)
    ? { center, zoom: solved.zoom }
    : null
}

/**
 * Polls until the fit can be read. Not a mobx `when`: the bounds live on the mapbox-gl source,
 * which is not observable. Gives up at the deadline, so a source that never loads reads as null.
 */
export const waitForTilesetFitCamera = async (
  read: () => ITilesetFitCamera,
  now: () => number = Date.now,
  wait: (ms: number) => Promise<void> = ms =>
    new Promise(resolve => setTimeout(resolve, ms)),
): Promise<ITilesetFitCamera> => {
  const deadline = now() + BOUNDS_WAIT_MS
  let fit = read()

  while (fit === null && now() < deadline) {
    await wait(BOUNDS_POLL_MS)
    fit = read()
  }

  return fit
}
